/**
 * HR Suite — hardcoded navigation manifest.
 *
 * DELIBERATELY NOT SERVER-DRIVEN. Unlike the CRM sidebar (which is built from
 * `Tab` rows via `GET /platform/tabs`), the HR Suite's nav lives here in the
 * client. That is a decision, not an oversight:
 *
 *   - No `Tab` seeding, no `TabType`/`appKey` migration, no COMPONENT_MAP
 *     string contract between server and client for HR.
 *   - `GET /platform/tabs` is left byte-identical, so the already-shipped
 *     mobile APKs are completely unaffected. HR simply does not appear there.
 *
 * HR OWNS EVERY ROUTE IT SHOWS. The suite never borrows a CRM route. Its
 * Automation, Reports and Settings screens are HR's own, under `/hr/*`, gated
 * on HR's own route keys — they are NOT the CRM's `/automation`, `/report` and
 * `/settings`. HR automations act on shifts, leave and payroll; CRM automations
 * act on leads. Sharing them would mean an HR admin configuring lead pipelines.
 *
 * RBAC IS STILL ENFORCED — ON CAPABILITIES. Every entry carries `requires`
 * (an HR module + action, e.g. hr_payroll_management.view). Nav visibility and
 * route access are gated on the caller's server-resolved levels from
 * `GET /suites/hr/me` (see `useHrNav` / `HrRouteGuard`), which are computed
 * from `access_config.global.hr_*` — the same map every `/suites/hr/*` endpoint
 * enforces server-side. The client gate is convenience; the server gate is
 * the boundary. HR access is NOT stored in `access_config.routeKeys`.
 *
 * `routeKey` stays on each entry as a stable identifier (React keys in the
 * sidebar); it grants nothing. Keys are still
 * append-only by convention so old links and analytics keep resolving.
 */

import type { HrMeResponse, HrModuleKey } from "../types/hr.contract";

export const HR_BASE_PATH = "/hr";

/**
 * The capability an entry needs (server-resolved, from GET /suites/hr/me).
 * Nav visibility and route access gate on THIS — the server enforces the same
 * capability on every endpoint behind the screen. "own" counts as access
 * (self-service screens show the caller's own rows).
 */
export interface HrRequirement {
  module: HrModuleKey;
  action: string;
}

/**
 * Where an entry renders in the sidebar.
 *  - `hr`         → the "HR" accordion group
 *  - `management` → the "CONFIGURATIONS" accordion group
 *  - `pinned`     → the footer slot, below everything
 */
export type HrNavGroup = "hr" | "management" | "pinned";

export interface HrNavItem {
  /** RBAC route key — permanent contract, see file header. */
  routeKey: string;
  label: string;
  /** Absolute path, always under HR_BASE_PATH. */
  path: string;
  /** lucide-react icon name. */
  icon: string;
  /** Which sidebar section it belongs to. */
  group: HrNavGroup;
  /** Shown in the HR sidebar. Hidden entries are routable but not listed. */
  showInNav: boolean;
  /** Capability gate — see HrRequirement. */
  requires: HrRequirement;
  /**
   * The screen opens for anyone who can open ANY HR screen, not for one
   * capability — `requires` is then only one of the many that open it (see
   * `hrEntryRequirements`). For a screen whose endpoint has no capability gate
   * of its own and answers with only what the caller may see.
   */
  anyHrAccess?: boolean;
  /**
   * Sidebar-only filter on top of the capability gate: the entry is LISTED
   * only while this answers true. The route stays reachable either way
   * (notifications link to it) and shows its own empty state.
   */
  navWhen?: (me: HrMeResponse) => boolean;
  /** Live count shown beside the label in the sidebar. 0 = no badge. */
  badge?: (me: HrMeResponse) => number;
}

/**
 * The caller decides other people's requests: a reporting manager, anyone with
 * a request waiting on them right now, or a role that approves leave / WFH or
 * edits attendance for everyone ("all" — "own" never approves anything).
 * Admins bypass, as everywhere.
 */
export function hrDecidesRequests(me: HrMeResponse): boolean {
  if (me.isAdmin || me.managesTeam || (me.pendingApprovals ?? 0) > 0) return true;
  const all = (module: HrModuleKey, action: string) =>
    me.access?.[module]?.[action] === "all";
  return (
    all("hr_leave_management", "approve") ||
    all("hr_wfh_management", "approve") ||
    all("hr_attendance_management", "edit")
  );
}

export const HR_NAV_ITEMS: readonly HrNavItem[] = [
  // ── HR ───────────────────────────────────────────────────────────────────
  {
    routeKey: "hr-dashboard",
    label: "HR dashboard",
    path: `${HR_BASE_PATH}/dashboard`,
    icon: "LayoutGrid",
    group: "hr",
    showInNav: true,
    requires: { module: "hr_dashboard", action: "view" },
  },
  {
    routeKey: "hr-attendance",
    label: "Attendance",
    path: `${HR_BASE_PATH}/attendance`,
    icon: "FileText",
    group: "hr",
    showInNav: true,
    requires: { module: "hr_attendance_management", action: "view" },
  },
  // Listed since HR v2: "Add employee" and "Import from team" were reachable
  // only through a typed URL — a dead end on day one. Still gated on its own
  // capability, so a role may use HR without seeing employee records; a
  // self-service user sees only their own record (the server scopes the list).
  {
    routeKey: "hr-people",
    label: "Employees",
    path: `${HR_BASE_PATH}/people`,
    icon: "Users",
    group: "hr",
    showInNav: true,
    requires: { module: "hr_employee_management", action: "view" },
  },
  // ONE list of everything waiting for the caller's decision — leave, WFH and
  // attendance fixes. GET /approvals has no capability gate of its own (it
  // answers with only what THIS caller may decide), and a reporting manager on
  // a self-service role decides their team's requests without holding any
  // approve capability — so the screen opens for any HR user (`anyHrAccess`).
  // It is LISTED only for people who decide requests, with the live count.
  {
    routeKey: "hr-approvals",
    label: "Approvals",
    path: `${HR_BASE_PATH}/approvals`,
    icon: "ClipboardCheck",
    group: "hr",
    showInNav: true,
    requires: { module: "hr_dashboard", action: "view" },
    anyHrAccess: true,
    navWhen: hrDecidesRequests,
    badge: (me) => me.pendingApprovals ?? 0,
  },
  {
    routeKey: "hr-payroll",
    label: "Payroll",
    path: `${HR_BASE_PATH}/payroll`,
    icon: "IndianRupee",
    group: "hr",
    showInNav: true,
    requires: { module: "hr_payroll_management", action: "view" },
  },
  {
    routeKey: "hr-analytics",
    label: "Analytics",
    path: `${HR_BASE_PATH}/analytics`,
    icon: "LineChart",
    group: "hr",
    showInNav: true,
    requires: { module: "hr_report_management", action: "view" },
  },

  // ── CONFIGURATIONS ───────────────────────────────────────────────────────
  // HR's own, not the CRM's. `hr-automation` rules fire on attendance, leave
  // and payroll events; `hr-reports` reads the HR register. Neither can see a
  // lead, and the CRM's equivalents cannot see an employee.
  {
    routeKey: "hr-automation",
    label: "Automation",
    path: `${HR_BASE_PATH}/automation`,
    icon: "Zap",
    group: "management",
    showInNav: true,
    requires: { module: "hr_automation_management", action: "view" },
  },
  {
    routeKey: "hr-reports",
    label: "Reports",
    path: `${HR_BASE_PATH}/reports`,
    icon: "BarChart3",
    group: "management",
    showInNav: true,
    requires: { module: "hr_report_management", action: "view" },
  },

  // ── Pinned ───────────────────────────────────────────────────────────────
  // HR's settings, pinned to the footer exactly like the CRM pins its own.
  // Keeping a second copy inside the HR group as well would just be the same
  // screen listed twice, so it lives here only.
  {
    routeKey: "hr-settings",
    label: "Settings",
    path: `${HR_BASE_PATH}/settings`,
    icon: "Settings",
    group: "pinned",
    showInNav: true,
    requires: { module: "hr_settings_management", action: "view" },
  },

  // ── Routable but not listed ──────────────────────────────────────────────
  // Hidden, but NOT merely cosmetic: leave and WFH are rendered as sub-tabs of
  // the Attendance page, yet approving leave is a categorically different
  // capability from reading an attendance register. Folding them under
  // `hr-attendance` would hand leave approval to every role that can see
  // attendance — the `editor` preset is deliberately denied leave, and would
  // have gained it. They keep their own keys; the sub-tabs are gated on them.
  {
    routeKey: "hr-leave",
    label: "Leave Management",
    path: `${HR_BASE_PATH}/leave`,
    icon: "CalendarDays",
    group: "hr",
    showInNav: false,
    requires: { module: "hr_leave_management", action: "view" },
  },
  {
    routeKey: "hr-wfh",
    label: "WFH Tracking",
    path: `${HR_BASE_PATH}/wfh`,
    icon: "Home",
    group: "hr",
    showInNav: false,
    requires: { module: "hr_wfh_management", action: "view" },
  },
] as const;

/** Every HR route key. Used by the RBAC role editor and the access back-fill. */
export const HR_ROUTE_KEYS: readonly string[] = HR_NAV_ITEMS.map(
  (i) => i.routeKey,
);

/** Landing route when the user enters the HR Suite without a specific target. */
export const HR_HOME_PATH = `${HR_BASE_PATH}/dashboard`;

/**
 * Accordion sections, in display order. Labels mirror the CRM's own groups.
 *
 * An empty label renders the section headerless — no title row, no collapse
 * chevron, items always shown. The primary `hr` group uses it: the suite
 * switcher directly above already says "HR", so a second "HR" heading was
 * only repeating itself.
 */
export const HR_NAV_SECTIONS: readonly { group: HrNavGroup; label: string }[] = [
  { group: "hr", label: "" },
  { group: "management", label: "CONFIGURATIONS" },
] as const;

/**
 * Sub-tabs of the Attendance screen.
 *
 * They LOOK like one page, but each carries its own `routeKey` because they are
 * not one capability. Attendance and Work Hours are two views of the same
 * register (`hr-attendance`); WFH and Leave are approval queues that grant real
 * authority over other people's time and pay, so they gate separately. A user
 * only ever sees the sub-tabs they hold the capability (`requires`) for.
 */
export const HR_ATTENDANCE_TABS = [
  {
    key: "attendance",
    label: "Attendance",
    icon: "FileText",
    routeKey: "hr-attendance",
    requires: { module: "hr_attendance_management", action: "view" } as HrRequirement,
  },
  {
    // The month at a glance (the muster) — the register seen from above.
    key: "month",
    label: "Month",
    icon: "CalendarRange",
    routeKey: "hr-attendance",
    requires: { module: "hr_attendance_management", action: "view" } as HrRequirement,
  },
  {
    key: "work-hours",
    label: "Work Hours",
    icon: "Clock",
    routeKey: "hr-attendance",
    requires: { module: "hr_attendance_management", action: "view" } as HrRequirement,
  },
  {
    key: "wfh",
    label: "WFH Tracking",
    icon: "Home",
    routeKey: "hr-wfh",
    requires: { module: "hr_wfh_management", action: "view" } as HrRequirement,
  },
  {
    key: "leave",
    label: "Leave Management",
    icon: "CalendarDays",
    routeKey: "hr-leave",
    requires: { module: "hr_leave_management", action: "view" } as HrRequirement,
  },
] as const;

export type HrAttendanceTabKey = (typeof HR_ATTENDANCE_TABS)[number]["key"];

/** True when a pathname is inside the HR Suite. */
export function isHrPath(pathname: string): boolean {
  return pathname === HR_BASE_PATH || pathname.startsWith(`${HR_BASE_PATH}/`);
}
